import { Box } from '@mui/material';
import React from 'react';

import routes from '@/assets/constants/routes-no.ts';
import { ArrowLink } from '@/competitions/_common/components/ArrowLink.tsx';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWithConfig } from '@/lib/api/requests.ts';
import { Timer, type TimerTheme } from '@/ui/Timer.tsx';

import { SEPTEMBER_2025_CAMPAIGN_END_DATE } from '../../../config.ts';
import { CTA } from '../_components/CTA.tsx';
import { Section } from '../_components/Section.tsx';

const dateFormatter = new Intl.DateTimeFormat('no', {
  day: 'numeric',
  month: 'long',
});
const dateStr = dateFormatter.format(SEPTEMBER_2025_CAMPAIGN_END_DATE);

const timerTheme: TimerTheme = {
  backgroundColor: '#355383',
  digitsColor: '#FFFFFF',
  labelsColor: '#F0F4FA',
};

export type TimerSectionProps = {
  activeCompetition: CompetitionWithConfig | undefined;
};

export const TimerSection: React.FC<TimerSectionProps> = (props) => {
  return (
    <Section
      sx={{
        gap: '20px',
        flexDirection: { md: 'row' },
        flexWrap: { md: 'wrap', xl: 'initial' },
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          textAlign: { xs: 'center', xl: 'left' },
          flex: { xl: 1.15 },
          width: { md: '100%', xl: 'auto' },
          mb: { xs: 4, md: 2 },
          pr: { xl: 8 },
          mt: { xl: 4 },
        }}
      >
        <div className="mb-6 typography-v1-h1">Over halvparten får lavere rente</div>

        <Box
          component="p"
          sx={{
            mb: 4,
            maxWidth: '70ch',
            mx: 'auto',
          }}
          className="typography-v1-body3"
        >
          Over halvparten av de som sjekker med Scoopr får tilbud om lavere rente på minst ett av
          sine lån.
          <br />
          <br />
          Lavere rente gjør det enklere å bli gjeldfri. Første steg er å få oversikt over din gjeld
          og dine renter.
          <br />
          <br />
          Gjennom våre trekninger har titusenvis av nordmenn gjort nettopp dette, og flere tusen har
          fått tilbud om lavere rente.
        </Box>

        <div className="flex items-end gap-8">
          <CTA
            activeCompetition={props.activeCompetition}
            onClick={() => {
              sendUserPortalEvent('sep25_competition_cta_timer_clicked');
            }}
          />
        </div>
      </Box>
      <div className="mx-auto flex max-w-sm flex-1 flex-col rounded-lg bg-[#EFF7FF] p-6 md:max-w-md lg:mx-0 lg:max-w-xl">
        <p className="mb-4 typography-v1-h2">Premie</p>
        <p className="typography-v1-h1">En heldig vinner</p>
        <p className="typography-v1-h3">får slettet inntil</p>
        <p className="typography-v1-h1">500 000 kr</p>
        <p className="typography-v1-h3">av sin rentebærende forbruks- og kredittkortgjeld</p>

        <ArrowLink
          to={routes.SEPTEMBER_2025_COMPETITION_TERMS}
          sx={{ display: 'block', my: 2, mt: 'auto' }}
        >
          Vilkår og betingelser
        </ArrowLink>
      </div>
      <div className="mx-auto flex max-w-sm flex-1 flex-col rounded-lg bg-[#031839] p-6 text-white md:max-w-md lg:mx-0 lg:max-w-xl">
        <p className="typography-v1-h2">Trekningsdato</p>
        <Box
          component="p"
          sx={{ fontSize: { lg: '3.812rem' }, mb: 2 }}
          className="typography-v1-display"
        >
          {dateStr}
        </Box>
        <Timer date={SEPTEMBER_2025_CAMPAIGN_END_DATE} theme={timerTheme} />

        <Box
          component="p"
          sx={{
            mb: 2,
            mt: { xs: 3, md: 4, xl: 6 },
          }}
          className="typography-v1-body3"
        >
          Vinnerne kontaktes via e-post og mobil.
          <br />
          Vi tar hensyn til personvern og publiserer ikke vinnere uten samtykke.
        </Box>

        <ArrowLink
          to={routes.SEPTEMBER_2025_COMPETITION_TERMS}
          sx={{ display: 'block', my: 2, mt: 'auto' }}
        >
          Vilkår og betingelser
        </ArrowLink>
      </div>
    </Section>
  );
};
